import { React } from 'react';
import { motion } from 'framer-motion';

const CHECK_PATH = 'M4.5 12.5l5 5 10-11';
const GREEN = '#2F855A'; // chakra green.600
const RING_GREEN = '#38A169'; // chakra green.500

const SPARKLE_COUNT = 6;

/*
 * The checkmark inside a check-in cell.
 *
 * `burst` is true only for the cell the user just clicked: that one gets the
 * full reward — the check draws itself in while popping past full size, a
 * ring ripples outward, and a handful of sparkles scatter. Cells that were
 * already checked when the page rendered get the static check, so a full list
 * doesn't fire a wall of animations on load.
 */
function CheckBurst({ burst }) {
  if (!burst) {
    return (
      <svg viewBox='0 0 24 24' width='1em' height='1em' aria-hidden='true'>
        <path
          d={CHECK_PATH}
          fill='none'
          stroke={GREEN}
          strokeWidth='3.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    );
  }

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        width: '1em',
        height: '1em',
      }}
    >
      {/* Ring scales past the button edge on purpose — the splash spilling
          outside the square is what sells the impact. */}
      <motion.span
        initial={{ scale: 0.3, opacity: 0.8 }}
        animate={{ scale: 3.5, opacity: 0 }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: `2px solid ${RING_GREEN}`,
        }}
      />
      {Array.from({ length: SPARKLE_COUNT }, (_, i) => {
        const angle = (i / SPARKLE_COUNT) * 2 * Math.PI - Math.PI / 2;
        return (
          <motion.span
            key={i}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{
              x: Math.cos(angle) * 18,
              y: Math.sin(angle) * 18,
              opacity: 0,
              scale: 0.3,
            }}
            transition={{ duration: 0.45, ease: 'easeOut', delay: 0.05 }}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 4,
              height: 4,
              marginLeft: -2,
              marginTop: -2,
              borderRadius: '50%',
              background: RING_GREEN,
              pointerEvents: 'none',
            }}
          />
        );
      })}
      <motion.svg
        viewBox='0 0 24 24'
        width='1em'
        height='1em'
        aria-hidden='true'
        initial={{ scale: 0.4 }}
        animate={{ scale: [0.4, 1.55, 1] }}
        transition={{ duration: 0.4, times: [0, 0.6, 1], ease: 'easeOut' }}
      >
        <motion.path
          d={CHECK_PATH}
          fill='none'
          stroke={GREEN}
          strokeWidth='3.5'
          strokeLinecap='round'
          strokeLinejoin='round'
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.28, ease: 'easeOut', delay: 0.05 }}
        />
      </motion.svg>
    </span>
  );
}

export { CheckBurst };
